/** Cargo Heating onboarding — segment rules (MR excluded; MR2 preserved exactly). */

import { resolveSegmentFromVessel } from './cargo-heating-vessel-mapper.util';

export interface SegmentDropdownOption {
  label: string;
  value: string;
}

export function normalizeExactSegmentToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
}

/** True only for exact legacy `MR`, not `MR2` or partial matches. */
export function isLegacyMrSegment(value: unknown): boolean {
  return normalizeExactSegmentToken(value) === 'MR';
}

export function filterCargoHeatingSegmentOptions<T extends SegmentDropdownOption>(
  options: T[],
): T[] {
  return options.filter(
    (option) => normalizeExactSegmentToken(option.value) !== 'MR',
  );
}

/**
 * Canonical segment for cargo heating: exact API value (e.g. MR2), never MR.
 */
export function resolveCargoHeatingCanonicalSegment(
  source: Record<string, unknown> | null | undefined,
  fallback = '',
): string {
  const fromApi = resolveSegmentFromVessel(source ?? {});
  const candidate = (fromApi || String(fallback ?? '').trim()).trim();
  if (!candidate) {
    return '';
  }
  if (isLegacyMrSegment(candidate)) {
    console.warn(
      '[Cargo Heating] Legacy segment MR is not supported for new onboarding.',
      source,
    );
    return '';
  }
  return candidate;
}

export function matchCargoHeatingSegmentToOptions(
  segment: string,
  options: SegmentDropdownOption[],
): string {
  const raw = String(segment ?? '').trim();
  if (!raw || isLegacyMrSegment(raw)) {
    return '';
  }

  const token = raw.toLowerCase().replace(/\s+/g, '');
  const match = options.find((option) => {
    const value = String(option.value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    const label = String(option.label ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    return value === token || label === token;
  });

  return match?.value ?? raw;
}
